import React from 'react';
import { Navigate } from 'react-router-dom';
import Layouts from '../../Layout';
import CreatorMapRouteFallback from '../../pages/CreatorMap/CreatorMapRouteFallback';
import LegacyDetailRedirect from '../../components/routing/LegacyDetailRedirect';

const HomePage = React.lazy(() => import('../../pages/LandingPage/HomePage'));
const SignUpPage = React.lazy(() => import('../../pages/auth/signup'));
const LoginPage = React.lazy(() => import('../../pages/auth/Login'));
const TermsAndConditions = React.lazy(() => import('../../pages/Legal/TermsAndConditions'));
const PrivacyPolicy = React.lazy(() => import('../../pages/Legal/PrivacyPolicy'));
const CookieNotice = React.lazy(() => import('../../pages/Legal/CookieNotice'));
const ContactUs = React.lazy(() => import('../../pages/shared/ContactUs'));
const PaymentPage = React.lazy(() => import('../../pages/Payment/PaymentPage'));
const MFAVerification = React.lazy(() => import('../../pages/auth/MFAVerification'));
const FAQ = React.lazy(() => import('../../pages/shared/FAQ'));
const InfluencerMarketplace = React.lazy(() => import(
  /* webpackChunkName: "influencer-marketplace" */
  /* webpackPrefetch: true */
  '../../pages/Marketplace/influencer/InfluencerMarketplace'
));
const CreatorMap = React.lazy(() => import(
  /* webpackPrefetch: true */
  '../../pages/CreatorMap/CreatorMapSimple'
));
const InfluencerDetails = React.lazy(() => import('../../pages/Marketplace/influencer/InfluencerDetails'));
const CampaignMarketplace = React.lazy(() => import('../../pages/Marketplace/campaign/CampaignMarketplace'));
const CampaignDetails = React.lazy(() => import('../../pages/Marketplace/campaign/CampaignDetails'));
const Blog = React.lazy(() => import('../../pages/blog/Blog'));
const BlogPostDetail = React.lazy(() => import('../../pages/blog/BlogPostDetail'));
const ForgotPassword = React.lazy(() => import('../../pages/auth/ForgotPassword'));
const ResetPassword = React.lazy(() => import('../../pages/auth/ResetPassword'));

function LazyRouteFallback({ fullViewport = false }) {
  return <div style={{ minHeight: fullViewport ? '100vh' : '70vh' }} />;
}

function LazyPage({ Component, fullViewport = false }) {
  return (
    <React.Suspense fallback={<LazyRouteFallback fullViewport={fullViewport} />}>
      <Component />
    </React.Suspense>
  );
}

function LazyCreatorMap() {
  return (
    <React.Suspense fallback={<CreatorMapRouteFallback />}>
      <CreatorMap />
    </React.Suspense>
  );
}

function discoveryLayout(isAuthenticated, userType, audience, child) {
  if (audience === 'creator' && isAuthenticated && userType === 'brand') {
    return (
      <Layouts pageType="header" brand>
        {child}
      </Layouts>
    );
  }
  if (audience === 'campaign' && isAuthenticated && userType === 'influencer') {
    return (
      <Layouts pageType="header" influencer>
        {child}
      </Layouts>
    );
  }
  return (
    <Layouts pageType="header">
      {child}
    </Layouts>
  );
}

function influencerMarketplaceElement(isAuthenticated, userType) {
  if (isAuthenticated && userType === 'influencer') {
    return <Navigate to="/marketplace/campaigns" replace />;
  }
  if (isAuthenticated && userType === 'brand') {
    return (
      <Layouts pageType="header" brand>
        <LazyPage Component={InfluencerMarketplace} />
      </Layouts>
    );
  }
  return (
    <Layouts pageType="header">
      <LazyPage Component={InfluencerMarketplace} />
    </Layouts>
  );
}

const publicRoutes = (isAuthenticated, userType) => [
  { path: '/', element: <LazyPage Component={HomePage} />, layout: 'header' },
  {
    path: '/login',
    element: isAuthenticated
      ? <Navigate to={userType === 'admin' ? '/admin/overview' : '/dashboard/overview'} />
      : <LazyPage Component={LoginPage} fullViewport />,
    layout: 'blankWithFooter',
  },
  { path: '/sign-up', element: <LazyPage Component={SignUpPage} fullViewport />, layout: 'blankWithFooter' },
  { path: '/signup', element: <LazyPage Component={SignUpPage} fullViewport />, layout: 'blankWithFooter' }, // Legacy redirect
  { path: '/terms-and-conditions', element: <LazyPage Component={TermsAndConditions} />, layout: 'header' },
  { path: '/legal/terms', element: <LazyPage Component={TermsAndConditions} />, layout: 'header' }, // Legacy redirect
  { path: '/privacy-policy', element: <LazyPage Component={PrivacyPolicy} />, layout: 'header' },
  { path: '/legal/privacy', element: <LazyPage Component={PrivacyPolicy} />, layout: 'header' }, // Legacy redirect
  { path: '/cookie-notice', element: <LazyPage Component={CookieNotice} />, layout: 'header' },
  { path: '/contact-us', element: <LazyPage Component={ContactUs} />, layout: 'header' },
  { path: '/contact', element: <LazyPage Component={ContactUs} />, layout: 'header' }, // Legacy redirect
  { path: '/pricing', element: <LazyPage Component={PaymentPage} />, layout: 'header' },
  { path: '/mfa-verification', element: <LazyPage Component={MFAVerification} fullViewport />, layout: 'blankWithFooter' },
  { path: '/faq', element: <LazyPage Component={FAQ} />, layout: 'header' },
  { path: '/blog', element: <LazyPage Component={Blog} />, layout: 'header' },
  { path: '/blog/:slug', element: <LazyPage Component={BlogPostDetail} />, layout: 'header' },
  { path: '/forgot-password', element: <LazyPage Component={ForgotPassword} />, layout: 'header' },
  { path: '/reset-password/:token', element: <LazyPage Component={ResetPassword} />, layout: 'header' },
  {
    path: '/marketplace/influencers',
    element: influencerMarketplaceElement(isAuthenticated, userType),
  },
  {
    path: '/influencers', // Legacy redirect
    element: influencerMarketplaceElement(isAuthenticated, userType),
  },
  {
    path: '/marketplace', // Legacy redirect
    element: influencerMarketplaceElement(isAuthenticated, userType),
  },
  {
    path: '/creator-map',
    element: isAuthenticated && userType === 'brand'
      ? (
        <Layouts pageType="header" brand>
          <LazyCreatorMap />
        </Layouts>
      )
      : (
        <Layouts pageType="header">
          <LazyCreatorMap />
        </Layouts>
      ),
  },
  {
    path: '/marketplace/influencers/:name/dp/:id',
    element: discoveryLayout(
      isAuthenticated,
      userType,
      'creator',
      <LazyPage Component={InfluencerDetails} />,
    ),
  },
  {
    path: '/marketplace/influencers/dp/:id',
    element: discoveryLayout(
      isAuthenticated,
      userType,
      'creator',
      <LazyPage Component={InfluencerDetails} />,
    ),
  },
  {
    path: '/marketplace/influencers/:slug',
    element: discoveryLayout(
      isAuthenticated,
      userType,
      'creator',
      <LegacyDetailRedirect basePath="/marketplace/influencers" />,
    ),
  },
  {
    path: '/marketplace/campaigns',
    element: discoveryLayout(
      isAuthenticated,
      userType,
      'campaign',
      <LazyPage Component={CampaignMarketplace} />,
    ),
  },
  {
    path: '/marketplace/campaigns/:name/dp/:id',
    element: discoveryLayout(
      isAuthenticated,
      userType,
      'campaign',
      <LazyPage Component={CampaignDetails} />,
    ),
  },
  {
    path: '/marketplace/campaigns/dp/:id',
    element: discoveryLayout(
      isAuthenticated,
      userType,
      'campaign',
      <LazyPage Component={CampaignDetails} />,
    ),
  },
  {
    path: '/marketplace/campaigns/:slug',
    element: discoveryLayout(
      isAuthenticated,
      userType,
      'campaign',
      <LegacyDetailRedirect basePath="/marketplace/campaigns" />,
    ),
  },
];

export default publicRoutes;
